/* eslint-disable complexity */
import React, { Component, Fragment } from 'react';
import { withNamespaces } from 'react-i18next';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import DeleteAltIcon from '@material-ui/icons/DeleteOutline';
import pluralize from 'pluralize';
import TableStyles from './Table.Styles';
import classnames from 'classnames';
import {
  PagingState,
  CustomPaging,
  SelectionState,
  SortingState,
  IntegratedSelection,
  IntegratedPaging,  
} from '@devexpress/dx-react-grid';
import { TableRow } from './TableRow';
import LoadingIndicator from '../../LoadingIndicator';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import ConfirmDialogModal from '../Modals/ConfirmDialog';
import { getGlobalProps } from '../../../utils/queryHelpers';
import _isEmpty from 'lodash/isEmpty';

import {
  DateFormatter,
  BlankFieldFormatter,
  CurrencyFormatter
} from './DataFormatters';

import {
  Grid,
  Table as DvExTable,
  TableHeaderRow,
  ColumnChooser,
  TableColumnVisibility,
  Toolbar,
  DragDropProvider,
  TableColumnReordering,
  PagingPanel,
  TableSelection
} from '@devexpress/dx-react-grid-material-ui';

import AddComponentsPlugin from './AddComponentsPlugin';

export class Table extends Component {
  static propTypes = {
    columns: PropTypes.array.isRequired,
    currencyColumns: PropTypes.array, // Does not apply if cellComponent is specified
    dateColumns: PropTypes.array, // Does not apply if cellComponent is specified
    dateFormat: PropTypes.string, // Does not apply if cellComponent is specified
    valueIfBlank: PropTypes.string, // Does not apply if cellComponent is specified
    data: PropTypes.array.isRequired,
    onChangePage: PropTypes.func,
    onChangePageRowsPerPage: PropTypes.func,
    onSelectRow: PropTypes.func,
    onDelete: PropTypes.func,
    rowsSelected: PropTypes.array,
    rowsPerPage: PropTypes.number,
    rowsPerPageOptions: PropTypes.array,
    rowComponent: PropTypes.func,
    cellComponent: PropTypes.func,
    page: PropTypes.number,
    name: PropTypes.string,
    orderBy: PropTypes.string,
    order: PropTypes.string,
    totalCount: PropTypes.number,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.object,
    deleting: PropTypes.bool,
    withDownload: PropTypes.bool,
    onDownloadClick: PropTypes.func,
    withColumnChooser: PropTypes.bool,
    withSelection: PropTypes.bool,
    withServerPaging: PropTypes.bool,
    defaultHiddenColumnNames: PropTypes.array,
    columnExtensions: PropTypes.array,
    withPagination: PropTypes.bool,
    lowercaseDeleteName: PropTypes.bool,
    sort: PropTypes.array,
    onSortChange: PropTypes.func
  };

  static defaultProps = {
    rowComponent: TableRow
  };

  state = {
    defaultHiddenColumnNames: this.props.defaultHiddenColumnNames,
    hiddenColumnNames: this.props.defaultHiddenColumnNames,
    pageSizes: [10, 25, 50, 100],
    currentPage: 0,
    columnOrder: this.props.columns.map(column => column.name),
    openConfirmDeleteNotificationModal: false
  };

  changeColumnOrder = newOrder => {
    this.setState({ columnOrder: newOrder });
  };

  getTheme = globalProps => {
    const theme = createMuiTheme({
      typography: {
        useNextVariants: true,
        color: {
          main: 'rgba(12,31,44,0.87)',
          second: 'rgba(12,31,44,0.6)'
        },
        font: {
          main: 'Roboto',
          second: 'Rubik'
        }
      }
    });

    if (_isEmpty(globalProps) || _isEmpty(globalProps.branding)) {
      return theme;
    }

    const { frontendPrimaryColor } = globalProps.branding;

    theme.palette.primary.main = frontendPrimaryColor;
    theme.palette.secondary.main = frontendPrimaryColor;
    return theme;
  };

  _handleChangeCurrentPage = currentPage => this.setState({ currentPage });

  _handleChangePageSize = pageSize => this.setState({ pageSize });

  getHeader = columns =>
    columns.map(column => ({ label: column.title, key: column.name }));

  _handleCloseConfirmDeleteNotificationModal = () =>
    this.setState({ openConfirmDeleteNotificationModal: false });

  _handleOpenConfirmDeleteNotificationModal = () =>
    this.setState({ openConfirmDeleteNotificationModal: true });

  _handleOnConfirm = () => {
    this._handleCloseConfirmDeleteNotificationModal();
    this.props.onDelete(this.props.rowsSelected);
  };

  _formatItemName = () => {
    const { name, rowsSelected, lowercaseDeleteName = true } = this.props;
    if (!rowsSelected) {
      return name;
    }
    return lowercaseDeleteName
      ? pluralize(name, rowsSelected.length).toLowerCase()
      : pluralize(name, rowsSelected.length);
  };


  render() {
    const {
      t,
      classes,
      columns = [],
      data = [],
      name = '',
      totalCount,
      rowsPerPage,
      page,
      onChangePage,
      onChangePageRowsPerPage,
      rowComponent,
      cellComponent = null,
      loading,
      error,
      rowsSelected = [],
      onSelectRow,
      deleting,
      withDownload = false,
      onDownloadClick,
      withColumnChooser = true,
      withSelection = false,
      withServerPaging = true,
      defaultHiddenColumnNames = [],
      withPagination = true,
      customHeaderAction,
      columnExtensions = [],
      filterToolbar,
      children,
      currencyColumns = [],
      dateColumns = [],
      dateFormat,
      valueIfBlank,
      sort = null,
      onSortChange
    } = this.props;
    const globalProps = getGlobalProps();
    const tableTheme = this.getTheme(globalProps);

    const rows = loading ? [{}] : data;
    const hasSelectedRows = rowsSelected.length > 0;

    const itemName = this._formatItemName();

    const { columnOrder } = this.state;

    let devExTableProps = { columnExtensions, rowComponent };
    if (cellComponent) devExTableProps = { cellComponent, ...devExTableProps };
    const noDataMessage = error
      ? t('Uh, oh.  There was a problem loading your data.')
      : t('No Data');

    return (
      <Fragment>
        <Paper
          className={classnames(classes.root, {
            [classes.rootWithoutPagination]: !withPagination
          })}
        >
          <MuiThemeProvider theme={tableTheme}>
            <Grid rows={rows} columns={columns}>
              {sort && (
                <SortingState sorting={sort} onSortingChange={onSortChange} />
              )}
              {Boolean(valueIfBlank) && (
                <BlankFieldFormatter
                  valueIfBlank={valueIfBlank}
                  for={columns.map(c => c.name)}
                />
              )}

              {Boolean(dateColumns && dateColumns.length > 0) && (
                <DateFormatter
                  valueIfBlank={valueIfBlank}
                  for={dateColumns}
                  format={dateFormat}
                />
              )}
              {Boolean(currencyColumns && currencyColumns.length > 0) && (
                <CurrencyFormatter
                  valueIfBlank={valueIfBlank}
                  for={currencyColumns}
                />
              )}
              <DragDropProvider />
              {withSelection && (
                <SelectionState
                  selection={rowsSelected}
                  onSelectionChange={onSelectRow}
                />
              )}
              {withSelection && <IntegratedSelection />}
              {withPagination && (
                <PagingState
                  currentPage={withServerPaging ? page : this.state.page}
                  defaultPageSize={rowsPerPage}
                  onCurrentPageChange={
                    withServerPaging
                      ? onChangePage
                      : this._handleChangeCurrentPage
                  }
                  onPageSizeChange={onChangePageRowsPerPage}
                />
              )}

              {withPagination && withServerPaging && (
                <CustomPaging totalCount={totalCount} />
              )}

              {withPagination && !withServerPaging && <IntegratedPaging />}
              <DvExTable
                {...devExTableProps}

                noDataCellComponent={props => (
                  <DvExTable.NoDataCell
                    {...props}
                    className={classnames({
                      [classes.noDataCell]: !withPagination
                    })}
                  />
                )}
                messages={{
                  noData: noDataMessage
                }}
              />
              <TableColumnReordering
                order={columnOrder}
                onOrderChange={this.changeColumnOrder}
              />
              {withPagination && (
                <PagingPanel pageSizes={this.state.pageSizes} />
              )}
              {withColumnChooser && (
                <TableColumnVisibility
                  defaultHiddenColumnNames={defaultHiddenColumnNames}
                />
              )}
              <TableHeaderRow showSortingControls={sort ? true : false} />
              <Toolbar
                rootComponent={props => (
                  <Toolbar.Root
                    className={classnames(classes.toolbarRoot, {
                      [classes.rowsSelectedToolbar]: hasSelectedRows
                    })}
                    {...props}
                  />
                )}
              />
           
              <AddComponentsPlugin
                templateName={'toolbarContent'}
                before={
                  <h1
                    className={classnames(classes.tableName, {
                      [classes.rowsSelectedTextColor]: hasSelectedRows
                    })}
                  >
                    {!loading &&
                      (hasSelectedRows ? (
                        `${rowsSelected.length} ${pluralize(
                          t('item'),
                          rowsSelected.length
                        )} ${t('selected')}`
                      ) : (
                        <div>
                          <p
                            className={classes.toolbarTitle}
                          >{`${totalCount} ${pluralize(name, totalCount)}`}</p>
                          {filterToolbar}
                        </div>
                      ))}
                  </h1>
                }
                after={() => {
                  if (!hasSelectedRows) {
                    return (
                      <Fragment>
                        {customHeaderAction}
                        {withDownload && (
                          <IconButton
                            aria-label="Download"
                            onClick={onDownloadClick}
                          >
                            <SaveAltIcon />
                          </IconButton>
                        )}
                      </Fragment>
                    );
                  }
                  if (hasSelectedRows) {
                    return (
                      <IconButton
                        aria-label="Delete"
                        className={classes.rowsSelectedButtonRoot}
                        onClick={this._handleOpenConfirmDeleteNotificationModal}
                      >
                        {!deleting ? (
                          <DeleteAltIcon
                            className={classes.rowsSelectedTextColor}
                          />
                        ) : (
                          <CircularProgress
                            className={classes.rowsSelectedDeleting}
                          />
                        )}
                      </IconButton>
                    );
                  }
                  return null;
                }}
              />
              {withColumnChooser && <ColumnChooser />}
              {withSelection && <TableSelection showSelectAll />}
              {children}
            </Grid>
          </MuiThemeProvider>
          {loading && <LoadingIndicator />}
        </Paper>
        <ConfirmDialogModal
          open={this.state.openConfirmDeleteNotificationModal}
          title={t(
            `Are you sure you want to remove ${
              rowsSelected.length > 1 ? 'these' : 'this'
            } ${itemName}?`
          )}
          message={t('This action cannot be undone.')}
          confirmLabel={t('Delete')}
          onClose={this._handleCloseConfirmDeleteNotificationModal}
          onConfirm={this._handleOnConfirm}
        />
      </Fragment>
    );
  }
}

export default withStyles(TableStyles)(withNamespaces()(Table));
